import { Minimatch } from 'minimatch'
import { defineNuxtPlugin, useRouter, useRoute, useRuntimeConfig } from '#app'

/**
 * @class Fb
 */
class Fb {
  constructor(options) {
    this.eventsQueue = []
    this.fqbLoaded = false
    this.options = options
    this.fbq = null

    this.isEnabled = !options.disabled
  }

  setFbq(fbq) {
    this.fbq = fbq
    this.fqbLoaded = true
    this.send()
  }

  setPixelId(pixelId) {
    // 避免設置 null 或 undefined 的 pixelId
    if (!pixelId) {
      if (this.options.debug) log('Skipping setPixelId: pixelId is null or undefined')
      return
    }
    this.options.pixelId = pixelId
    this.init()
  }

  /**
   * @method setUserData
   * Used to set user data that'll be used once the `fbq` init function is called.
   * @param {object} [userData] See https://developers.facebook.com/docs/facebook-pixel/advanced/advanced-matching#reference
   */
  setUserData(userData) {
    this.userData = userData
  }

  /**
   * @method enable
   */
  enable() {
    this.isEnabled = true
    this.init()
    this.track()
  }

  /**
   * @method disable
   */
  disable() {
    this.isEnabled = false
  }

  /**
   * @method init
   */
  init() {
    // 避免使用 null 或 undefined 的 pixelId
    if (!this.options.pixelId) {
      if (this.options.debug) log('Skipping init: pixelId is not set')
      return
    }
    this.query('init', this.options.pixelId, this.userData || undefined)
  }

  /**
   * @method track
   */
  track(event = null, parameters = null, eventID = null) {
    if (!event) {
      event = this.options.track
    }
    // 對於 track 事件，不需要 pixelId（由 init 設置）
    if (!eventID) {
      this.query('track', event, parameters)
    } else {
      this.query('track', event, parameters, eventID)
    }
  }

  /**
   * @method query
   * @param {string} cmd
   * @param {object} option
   * @param {object} parameters
   * @param {object} eventID
   */
  query(cmd, option, parameters = null, eventID = null) {
    // if (this.options.debug) log('Command:', cmd, 'Option:', option, 'Additional parameters:', parameters, 'EventID:', eventID)
    if (!this.isEnabled) return

    // 如果是 init 命令但 option (pixelId) 為 null，則跳過
    if (cmd === 'init' && !option) {
      // if (this.options.debug) log('Skipping init command: pixelId is null or undefined')
      return
    }

    this.eventsQueue.push({
      cmd,
      option,
      parameters,
      eventID,
    })

    this.send()
  }

  send() {
    if (!this.fqbLoaded) {
      return
    }

    while (this.eventsQueue.length) {
      let event = this.eventsQueue.shift()

      // if (this.options.debug) log('Send event: ', event)

      if (event.eventID) {
        this.fbq(event.cmd, event.option, event.parameters, event.eventID)
      } else if (event.parameters) {
        this.fbq(event.cmd, event.option, event.parameters)
      } else {
        this.fbq(event.cmd, event.option)
      }
    }
  }
}

function getMatchingPixel(options, path) {
  if (!options.pixels || !Array.isArray(options.pixels)) {
    return null
  }
  return options.pixels.find((pixel) => {
    const routeIndex = pixel.routes.findIndex((route) => {
      const minimatch = new Minimatch(route)
      return minimatch.match(path)
    })

    return routeIndex !== -1
  })
}

function log(...messages) {
  console.info.apply(this, ['[nuxt-facebook-pixel-module]', ...messages])
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const route = useRoute()
  const router = useRouter()

  const parsedOptions = config.public.facebookPixel || {
    pixelId: null,
    track: 'PageView',
    version: '2.0',
    disabled: false,
    debug: false,
    dev: process.env.NODE_ENV !== 'production',
    autoPageView: true,
    pixels: [],
  }

  // 客戶端專用：優先從 Query 找
  if (typeof window !== 'undefined') {
    let urlParams = new URLSearchParams(window.location.search)
    let cachePixel = null
    try {
      cachePixel = (JSON.parse(window.localStorage.getItem('pix')) || {}).value
    } catch (error) {
      console.log(error)
    }
    const stalePixelId = urlParams.get('pix') || cachePixel
    if (!parsedOptions.pixelId && stalePixelId) {
      parsedOptions.pixelId = stalePixelId
    }
  }

  const isDev = parsedOptions.dev && !parsedOptions.debug

  if (isDev)
    log('You are running in development mode. Set "debug: true" in your nuxt.config.js if you would like to trigger tracking events in local.')

  const path = route.path
  const matchingPixel = getMatchingPixel(parsedOptions, path)
  const pixelOptions = Object.assign({}, matchingPixel || parsedOptions)

  const instance = new Fb(pixelOptions)

  // 檢查是否已經由 GTM 或其他來源載入了 Facebook Pixel
  let fbqAlreadyLoaded = false

  if (typeof window !== 'undefined') {
    fbqAlreadyLoaded = !!window.fbq

    if (fbqAlreadyLoaded) {
      // GTM 已載入，直接使用現有的 fbq，不再執行初始化
      if (parsedOptions.debug) {
        log('Facebook Pixel SDK already loaded (possibly by GTM), skipping SDK load and init')
        log('Current pixelId:', pixelOptions.pixelId)
      }
      // 立即設置 fbq，但不會觸發 init（因為 pixelId 可能為 null）
      instance.setFbq(window.fbq)
    } else {
      // SDK 尚未載入，由插件載入並初始化
      ;((f, b, e, v, n, t, s) => {
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
        }
        if (!f._fbq) f._fbq = n
        n.push = n
        n.loaded = !0
        n.version = pixelOptions.version
        n.queue = []
        t = b.createElement(e)
        t.async = true
        t.defer = true
        t.src = v
        s = b.getElementsByTagName('body')[0]
        s.parentNode.appendChild(t, s)
        let onLoadCallback = () => {
          instance.setFbq(fbq)
          if (!isDev && !pixelOptions.disabled) {
            if (pixelOptions.manualMode) {
              fbq('set', 'autoConfig', false, pixelOptions.pixelId)
            }
            fbq('init', pixelOptions.pixelId)
            fbq('track', pixelOptions.track)
          }
        }
        if (t.readyState) {
          t.onreadystatechange = function () {
            if (t.readyState === 'loaded' || t.readyState === 'complete') {
              t.onreadystatechange = null
              onLoadCallback()
            }
          }
        } else {
          t.onload = onLoadCallback
        }
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
    }
  }

  router.afterEach((to) => {
    /**
     * Change the current pixelId according to the route.
     */
    const matchingPixel = getMatchingPixel(parsedOptions, to.path)
    const pixelOptions = Object.assign({}, matchingPixel || parsedOptions)

    // 只有在有 pixelId 且與當前不同時才切換
    if (pixelOptions.pixelId && pixelOptions.pixelId !== instance.options.pixelId) {
      instance.setPixelId(pixelOptions.pixelId)
    }

    /**
     * Automatically track PageView
     * 如果 GTM 已載入，則跳過自動追蹤以避免重複
     */
    if (parsedOptions.autoPageView && !fbqAlreadyLoaded) {
      instance.track('PageView')
    }
  })

  return {
    provide: {
      fb: instance,
    },
  }
})
